import React, { useState, useRef, useEffect, useCallback } from 'react';
import { retiredValues } from './retiredValues.js';

// emptyLabel: what the collapsed trigger says with nothing picked.
function MultiSelectEditor({ value, onChange, disabled, options, loading, collapsible, onRequestClose, emptyLabel = 'None' }) {
  const [search, setSearch] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [open, setOpen] = useState(!collapsible);
  const searchRef = useRef(null);
  const listRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (open && searchRef.current) searchRef.current.focus();
  }, [open]);

  // Close on a click outside the editor. In collapsible mode this collapses
  // back to the trigger; in panel/detail mode it asks the parent to close via
  // onRequestClose (which flushes the pending save and exits edit mode) — so
  // the dropdown stays open until the user clicks away, instead of auto-closing.
  useEffect(() => {
    if (collapsible && !open) return;
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        if (collapsible) setOpen(false);
        if (onRequestClose) onRequestClose();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [collapsible, open, onRequestClose]);

  const selectedIds = new Set(
    (Array.isArray(value) ? value : []).filter(v => v != null).map(v => String(v.id || v))
  );

  const selectedLabels = (Array.isArray(value) ? value : [])
    .filter(v => v != null)
    .map(v => v.name || v.value || v.id || v)
    .join(', ');

  // A held value whose option was disabled still gets a ticked row, so it
  // can be unticked (once) — see retiredValues.js.
  const filtered = [...retiredValues(value, options), ...(options || [])].filter(opt => {
    const label = opt.name || opt.value || '';
    return label.toLowerCase().includes(search.toLowerCase());
  }).sort((a, b) => {
    const aSelected = selectedIds.has(String(a.id || a.value));
    const bSelected = selectedIds.has(String(b.id || b.value));
    if (aSelected && !bSelected) return -1;
    if (!aSelected && bSelected) return 1;
    return 0;
  });

  // Reset highlight when search changes
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [search]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex < 0 || !listRef.current) return;
    const items = listRef.current.querySelectorAll('.field-editor-option');
    if (items[highlightedIndex]) {
      items[highlightedIndex].scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex]);

  const toggleOption = useCallback((opt) => {
    if (disabled) return;
    const optId = String(opt.id || opt.value);
    const currentArr = Array.isArray(value) ? value : [];
    if (selectedIds.has(optId)) {
      onChange(currentArr.filter(v => String(v.id || v) !== optId));
    } else {
      onChange([...currentArr, opt]);
    }
  }, [disabled, value, selectedIds, onChange]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape' && collapsible) {
      setOpen(false);
      return;
    }
    if (filtered.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => (prev + 1) % filtered.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => (prev <= 0 ? filtered.length - 1 : prev - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < filtered.length) {
        toggleOption(filtered[highlightedIndex]);
      }
    }
  }, [filtered, highlightedIndex, toggleOption, collapsible]);

  // Collapsible mode: show a trigger button when closed
  if (collapsible && !open) {
    return (
      <div
        className="multiselect-trigger"
        onClick={() => !disabled && setOpen(true)}
      >
        <span className="multiselect-trigger-text">
          {selectedLabels || emptyLabel}
        </span>
        <span className="multiselect-trigger-arrow">&#9662;</span>
      </div>
    );
  }

  return (
    <div className="field-editor-multiselect-wrapper" ref={wrapperRef}>
      <input
        ref={searchRef}
        type="text"
        className="field-editor-search-input"
        placeholder="Search..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
      />
      {loading ? (
        <div className="field-editor-loading">Loading options...</div>
      ) : (
        <div className="field-editor-options-list" ref={listRef}>
          {filtered.map((opt, idx) => {
            const optId = String(opt.id || opt.value);
            const checked = selectedIds.has(optId);
            return (
              <div
                key={optId}
                className={`field-editor-option ${checked ? 'selected' : ''} ${opt.retired ? 'retired' : ''} ${highlightedIndex === idx ? 'highlighted' : ''}`}
                title={opt.retired ? 'This option is disabled. Untick it to remove it — it cannot be picked again.' : undefined}
                onClick={() => toggleOption(opt)}
                onMouseEnter={() => setHighlightedIndex(idx)}
              >
                <input
                  type="checkbox"
                  className="field-editor-checkbox"
                  checked={checked}
                  onChange={() => toggleOption(opt)}
                  disabled={disabled}
                />
                <span className="field-editor-option-label">
                  {opt.name || opt.value}
                  {opt.retired && <span className="field-editor-option-retired"> (disabled)</span>}
                </span>
              </div>
            );
          })}
          {filtered.length === 0 && !loading && (
            <div className="field-editor-empty">No options found</div>
          )}
        </div>
      )}
    </div>
  );
}

export default MultiSelectEditor;
